import { useCallback, useEffect, useState } from 'react';
import { setFatalSink } from '../app/globalHandlers';
import { log } from '../app/logger';
import { resolveResume, SURFACE } from '../app/resume';
import { checkSchema } from '../app/schemaGate';
import { breakWorkflowOpenFrom } from './capture/surface';
import { SchemaRecoveryScreen } from './recovery/SchemaRecoveryScreen';
import { TabShellB } from './shell/TabShellB';
import { useThemeB } from './theme';

/**
 * B-5 — **B's outermost frame**: the fatal sink, and the shell selector.
 *
 * §2.2: the engine's resume machinery IS the shell selector at launch — `resolveResume()`
 * returns `{kind:'match'}` for a resumable match (→ the in-match takeover shell) and
 * `{kind:'home'}` otherwise (→ the tab shell). The read happens **once, at mount** (the
 * `AppShell` reasoning: every later change of shell is a boundary crossing this app performs —
 * B-8's `FullTime` hand-off — so it already knows the answer). Since B-8, every arm renders
 * `TabShellB`: the takeover and setup are its overlays, so the boundary crossings out of both
 * (FullTime → the match record; Begin → the takeover) are overlay swaps inside one shell
 * rather than state this component would have to be told about.
 *
 * ── Why the fatal sink lives here (B-0, kept) ────────────────────────────────
 *
 * OBS-1/NFR-8: an installed PWA at a pitch has no console, so an uncaught error must reach the
 * screen. `setFatalSink` is one process-wide slot, claimed once for the life of the app; the
 * notice renders as a banner **above** the shell rather than replacing it (A's `App.tsx`
 * discipline — a screen that silently swapped to an error page mid-use would throw away state
 * the error may not have touched).
 */

/**
 * B-21 added `recovery`. It is a **phase**, not a shell, and that placement is the point: the
 * schema question is settled before a shell is chosen, so there is no arm of the tab shell that has
 * to know about it and no surface that can be reached with the database unreadable underneath it.
 */
type Phase = 'resolving' | 'ready' | 'failed' | 'recovery';

/**
 * B-7 added the `setup` arm: `resolveResume` names the surface, and for an unstarted setup the
 * surface is `setup` (`defaultSurfaceFor`, or the hint `recordSurface` wrote on entry). That
 * arm renders the TAB shell opened onto the setup overlay — §2.4 puts match setup in the
 * out-of-match world (it is reached from Matches → New match), and before this arm existed a
 * kill mid-setup relaunched into the takeover placeholder with no way back: a dead end.
 * A resumable match on any OTHER surface (post-Begin pre-kickoff included — the hint is
 * `discovery` by then) lands on the takeover overlay — B-8's real shell since it landed.
 */
type Shell =
  | { readonly kind: 'tabs' }
  | { readonly kind: 'setup'; readonly matchId: string }
  | {
      readonly kind: 'takeover';
      readonly matchId: string;
      /**
       * B-31/§21.2 — the takeover's one restored navigation param. `resolveResume` has always
       * returned `surfaceParams`; until B-31 this component threw them away on the line below.
       */
      readonly workflowOpen: boolean;
    };

const MODULE = 'ui-b/app';

export function AppB() {
  const [fatal, setFatal] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('resolving');
  const [shell, setShell] = useState<Shell>({ kind: 'tabs' });
  /**
   * Completed resolves, rendered as `data-resolved` — the `AppShell` device: the root element
   * exists during `resolving` too, so a waiter that needs "the shell has finished reading where
   * to go" has an instant a contract guarantees rather than a sleep.
   */
  const [resolved, setResolved] = useState(0);
  /**
   * B-19 — the stored theme, read once and applied to the document element. It lives at this
   * level for the same reason the fatal sink does: it is a property of the whole app, claimed
   * once for its life, and the More tab's own writes go straight to the same document attribute.
   */
  const theme = useThemeB();

  useEffect(() => {
    setFatalSink(setFatal);
    return () => {
      setFatalSink(null);
    };
  }, []);

  const resolve = useCallback((): void => {
    void (async () => {
      try {
        /**
         * B-21 — §13.5, **before** anything reads a row.
         *
         * `resolveResume()` is the first storage touch on B's boot path, so on a device whose data
         * is newer than this bundle it was the thing that failed, and the user was told the resume
         * pointer could not be read. That sentence is true and useless: the pointer is fine, the app
         * is out of date, and the engine has been promising a screen that says so since U-6.
         * `checkSchema()` never rejects, so this adds no failure mode to the normal boot — on a
         * healthy device it is one `open()` that would have happened on the next line anyway.
         */
        if ((await checkSchema()) === 'newer-than-app') {
          setPhase('recovery');
          return;
        }

        const target = await resolveResume();
        setShell(
          target.kind !== 'match'
            ? { kind: 'tabs' }
            : target.surface === SURFACE.setup
              ? { kind: 'setup', matchId: target.match.id }
              : {
                  kind: 'takeover',
                  matchId: target.match.id,
                  /**
                   * §21.2 — a hint that names another match is discarded WHOLE by `resolveResume`
                   * (`surface` and `surfaceParams` drop together in one ternary), and an
                   * unresumable match returns `{kind:'home'}` before the hint is read at all. So
                   * there is no state in which a rejected hint's params reach this line.
                   */
                  workflowOpen: breakWorkflowOpenFrom(target.surfaceParams),
                },
        );
        setPhase('ready');
      } catch (cause) {
        log.exception(MODULE, 'SES-3: the resume pointer could not be read', cause);
        setPhase('failed');
      } finally {
        setResolved((n) => n + 1);
      }
    })();
  }, []);

  useEffect(resolve, [resolve]);

  return (
    <div
      className="app-b"
      data-testid="app-b"
      data-shell={phase === 'ready' ? shell.kind : phase}
      data-resolved={String(resolved)}
      /* An unset preference sets NO attribute on the document, so "the theme has been applied"
         would otherwise be unobservable. This counter is the instant a pass waits on. */
      data-theme-read={String(theme.reads)}
    >
      {fatal !== null && (
        <div className="app-b__fatal" data-testid="fatal-error-b" role="alert">
          <strong>Uncaught error</strong>
          <br />
          {fatal}
        </div>
      )}
      {renderShell()}
    </div>
  );

  function renderShell() {
    if (phase === 'resolving')
      return <p className="homeb-empty">Finding where you left off…</p>;
    // §13.5 replaces the shell rather than banner-ing over it — the opposite call to the fatal sink
    // above, and for the opposite reason: a fatal error may not have touched the state on screen,
    // whereas here every tab behind it would be reading a database this bundle refuses to open.
    if (phase === 'recovery') return <SchemaRecoveryScreen />;
    if (phase === 'failed')
      return (
        <p className="homeb-empty" role="alert">
          Touchline could not read where you left off. The log has the detail.
        </p>
      );
    if (shell.kind === 'takeover')
      return (
        <TabShellB
          initialOverlay={{
            kind: 'takeover',
            matchId: shell.matchId,
            workflowOpen: shell.workflowOpen,
          }}
        />
      );
    if (shell.kind === 'setup') return <TabShellB initialOverlay={{ kind: 'setup' }} />;
    return <TabShellB />;
  }
}
